<template>
  <div>
    <div class="d-flex align-items-center justify-content-center" >
      <button class="btn btn-sm btn-outline-primary me-2" :disabled="displayedYear === currentYear" @click="prevYear">{{ displayedYear - 1 }}</button>
      <h2 class="mb-0 mx-2">{{ displayedYear }}</h2>
      <button class="btn btn-sm btn-outline-primary" :disabled="displayedYear === currentYear + 1" @click="nextYear">{{ displayedYear + 1 }}</button>
    </div>
    <div class="d-flex justify-content-between align-items-center mb-2">
        <button class="btn btn-sm btn-outline-primary" :disabled="currentPage === 0" @click="prevPage">
          <feather-icon icon="ChevronLeftIcon" size="14" />
        </button>
        <button class="btn btn-sm btn-outline-primary" :disabled="currentPage === 1" @click="nextPage">
          <feather-icon icon="ChevronRightIcon" size="14" />
        </button>
    </div>
    <div v-for="(monthRow, rowIndex) in pagedMonthsInRows" :key="rowIndex" class="mb-4">
      <div class="d-flex">
        <div v-for="(month, mIdx) in monthRow" :key="mIdx" class="flex-fill px-1">
          <h4 class="text-center">{{ month.name }}</h4>
          <table class="table table-sm">
            <thead>
              <tr>
                <th v-for="(day, dIdx) in weekDays" :key="dIdx" class="text-center">{{ day }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(week, wIdx) in month.weeks" :key="wIdx">
                <td v-for="(day, dIdx) in week" :key="dIdx" class="text-center">
                  <span
                    v-if="day"
                    :id="`day-${month.index}-${day}`"
                    :class="getDayClasses(month.index, day)"
                  >{{ day }}</span>
                  <b-tooltip
                    v-if="day"
                    :target="`day-${month.index}-${day}`"
                    placement="top"
                    custom-class="vacation-tooltip"
                  >
                    <div v-html="getTooltipHtml(month.index, day)"></div>
                  </b-tooltip>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Leyenda -->
    <div class="legend-container mt-3">
      <div class="d-flex flex-wrap justify-content-center align-items-center">
        <div class="legend-item">
          <span class="legend-box today"></span>
          <span class="legend-label">Hoy</span>
        </div>
        <div class="legend-item">
          <span class="legend-box holiday-day"></span>
          <span class="legend-label">Festivos</span>
        </div>
        <div class="legend-item">
          <span class="legend-box vacation-day-approved"></span>
          <span class="legend-label">Aprobados</span>
        </div>
        <div class="legend-item">
          <span class="legend-box vacation-day-pending"></span>
          <span class="legend-label">Pendientes</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { BTooltip } from 'bootstrap-vue'

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
]
const WEEK_DAYS = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

function getWeeksOfMonth(year, month) {
  const weeks = [];
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  let week = new Array(7).fill(null);
  let dayOfWeek = (firstDay.getDay() + 6) % 7; // Lunes=0
  for (let day = 1; day <= lastDay.getDate(); day++) {
    week[dayOfWeek] = day;
    if (dayOfWeek === 6 || day === lastDay.getDate()) {
      weeks.push([...week]);
      week = new Array(7).fill(null);
    }
    dayOfWeek = (dayOfWeek + 1) % 7;
  }
  return weeks;
}

export default {
  name: 'VacationCalendar',
  components: {
    BTooltip,
  },
  props: {
    vacationRequests: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    const now = new Date()
    const year = now.getFullYear()
    const currentMonth = now.getMonth()
    return {
      currentYear: year,
      displayedYear: year,
      weekDays: WEEK_DAYS,
      months: [],
      currentPage: currentMonth < 6 ? 0 : 1,
      today: {
        year,
        month: currentMonth,
        day: now.getDate(),
      },
      monthsPerRow: 3,
      calendarData: null,
    }
  },
  computed: {
    ...mapGetters({
      myCalendar: 'calendar/getMyCalendar',
    }),
    pagedMonths() {
      const start = this.currentPage * 6;
      return this.months.slice(start, start + 6);
    },
    pagedMonthsInRows() {
      const rows = [];
      const months = this.pagedMonths;
      const perRow = this.monthsPerRow;
      for (let i = 0; i < months.length; i += perRow) {
        rows.push(months.slice(i, i + perRow));
      }
      return rows
    },
    holidaysByMonth() {
      // Devuelve un objeto: { [mes]: { [día]: true } }
      const result = {}
      if (!this.myCalendar || !Array.isArray(this.myCalendar.holidays)) {
        return result
      }
      this.myCalendar.holidays.forEach(holiday => {
        const date = new Date(holiday.date)
        if (date.getFullYear() === this.displayedYear) {
          const month = date.getMonth()
          const day = date.getDate()
          if (!result[month]) result[month] = {}
          result[month][day] = holiday.name
        }
      })
      return result
    },
    vacationDaysByMonth() {
      // Devuelve un objeto: { [mes]: { [día]: { status, request } } }
      const result = {};
      if (!Array.isArray(this.vacationRequests)) return result;
      this.vacationRequests.forEach(req => {
        if (!req.start_date || !req.end_date) return;
        const start = new Date(req.start_date);
        const end = new Date(req.end_date);
        // Solo marcar si el año coincide con el mostrado
        if (start.getFullYear() > this.displayedYear || end.getFullYear() < this.displayedYear) return;
        let d = new Date(Math.max(start, new Date(this.displayedYear, 0, 1)));
        const last = new Date(Math.min(end, new Date(this.displayedYear, 11, 31)));
        while (d <= last) {
          const m = d.getMonth();
          const day = d.getDate();
          if (!result[m]) result[m] = {};
          // Si hay varios requests para el mismo día, priorizar: denied > approved > pending
          const prevData = result[m][day];
          let status = req.status;
          if (prevData) {
            if (prevData.status === 'denied') status = 'denied';
            else if (prevData.status === 'approved' && status !== 'denied') status = 'approved';
            else if (prevData.status === 'pending' && status === 'pending') status = 'pending';
          }
          result[m][day] = { status, request: req };
          d.setDate(d.getDate() + 1);
        }
      });
      return result;
    },
  },
  watch: {
    displayedYear: {
      immediate: true,
      handler(newYear) {
        this.months = MONTHS.map((name, idx) => ({
          name,
          index: idx,
          weeks: getWeeksOfMonth(newYear, idx),
        }))
        // Si el año cambia, y el mes actual no está en el año mostrado, resetear página a 0
        if (this.displayedYear !== this.today.year) {
          this.currentPage = 0
        } else {
          this.currentPage = this.today.month < 6 ? 0 : 1
        }
        // Cargar los festivos del año
        this.loadHolidays(newYear)
      },
    },
  },
  mounted() {
    this.updateMonthsPerRow();
    window.addEventListener('resize', this.updateMonthsPerRow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.updateMonthsPerRow);
  },
  methods: {
    ...mapActions({
      getMyCalendar: 'calendar/getMyCalendar',
    }),
    async loadHolidays(year) {
      try {
        await this.getMyCalendar(year)
      } catch (error) {
      }
    },
    updateMonthsPerRow() {
      const width = window.innerWidth;
      if (width < 768) {
        this.monthsPerRow = 1;
      } else if (width < 1750) {
        this.monthsPerRow = 2;
      } else {
        this.monthsPerRow = 3;
      }
    },
    isToday(monthIdx, day) {
      return (
        this.today.year === this.displayedYear &&
        this.today.month === monthIdx &&
        this.today.day === day
      );
    },
    getDayClasses(monthIdx, day) {
      const classes = []
      if (this.isToday(monthIdx, day)) {
        classes.push('today')
      }
      if (this.holidaysByMonth[monthIdx] && this.holidaysByMonth[monthIdx][day]) {
        classes.push('holiday-day')
      }
      const vacationData = this.vacationDaysByMonth[monthIdx] && this.vacationDaysByMonth[monthIdx][day]
      if (vacationData && vacationData.status === 'approved') {
        classes.push('vacation-day-approved')
      }
      if (vacationData && vacationData.status === 'pending') {
        classes.push('vacation-day-pending')
      }
      return classes
    },
    getTooltipText(monthIdx, day) {
      // Verificar si es un día festivo
      if (this.holidaysByMonth[monthIdx] && this.holidaysByMonth[monthIdx][day]) {
        return this.holidaysByMonth[monthIdx][day]
      }

      // Verificar si es un día de vacaciones (aprobado o pendiente)
      const vacationData = this.vacationDaysByMonth[monthIdx] && this.vacationDaysByMonth[monthIdx][day]
      if (vacationData && vacationData.request) {
        const req = vacationData.request
        const typeName = req.type && req.type.name ? req.type.name : 'Vacaciones'

        const startDate = new Date(req.start_date)
        const endDate = new Date(req.end_date)

        const startDay = this.formatDayWithWeekday(startDate)
        const endDay = this.formatDayWithWeekday(endDate)

        // Si es el mismo día - separar el tipo del resto con salto de línea
        if (req.start_date === req.end_date) {
          return `${typeName}\n${startDay}`
        }

        // Si son diferentes días - separar el tipo del resto con salto de línea
        return `${typeName}\n${startDay} - ${endDay}`
      }

      // Por defecto, mostrar el día
      return `Día ${day} de ${this.months[monthIdx].name}`
    },
    getTooltipHtml(monthIdx, day) {
      // Verificar si es un día festivo
      if (this.holidaysByMonth[monthIdx] && this.holidaysByMonth[monthIdx][day]) {
        return this.holidaysByMonth[monthIdx][day]
      }

      // Verificar si es un día de vacaciones (aprobado o pendiente)
      const vacationData = this.vacationDaysByMonth[monthIdx] && this.vacationDaysByMonth[monthIdx][day]
      if (vacationData && vacationData.request) {
        const req = vacationData.request
        const typeName = req.type && req.type.name ? req.type.name : 'Vacaciones'

        const startDate = new Date(req.start_date)
        const endDate = new Date(req.end_date)

        const startDay = this.formatDayWithWeekday(startDate)
        const endDay = this.formatDayWithWeekday(endDate)

        // Si es el mismo día
        if (req.start_date === req.end_date) {
          return `<strong>${typeName}</strong><br>${startDay}`
        }

        // Si son diferentes días
        return `<strong>${typeName}</strong><br>${startDay} - ${endDay}`
      }

      // Por defecto, mostrar el día
      return `Día ${day} de ${this.months[monthIdx].name}`
    },
    formatDayWithWeekday(date) {
      const weekdays = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado']
      const months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']

      const weekday = weekdays[date.getDay()]
      const day = date.getDate()
      const month = months[date.getMonth()]

      return `${weekday} ${day} ${month}`
    },
    nextPage() {
      if (this.currentPage < 1) this.currentPage++;
    },
    prevPage() {
      if (this.currentPage > 0) this.currentPage--;
    },
    nextYear() {
      if (this.displayedYear < this.currentYear + 1) {
        this.displayedYear++;
      }
    },
    prevYear() {
      if (this.displayedYear > this.currentYear) {
        this.displayedYear--;
      }
    },
  },
};
</script>

<style scoped>
table {
  width: 100%;
  margin-bottom: 0;
}
th, td {
  background-color: transparent!important;
  border: none!important;
  width: 40px;
  min-width: 40px;
  height: 2.2em;
  padding: 0em !Important;
  box-sizing: border-box;
}
th{
  font-size: 1rem!important;
}
.today {
  background: rgba(156, 206, 217, 0.56);
  color: #608fd3;
  border-radius: 100px;
  padding: 0.2em 0.6em;
  cursor: pointer;
}
.holiday-day {
  background: #ffd4a3;
  color: #d97706;
  border-radius: 100px;
  padding: 0.1em 0.3em;
  box-sizing: border-box;
  border: 1px solid #ffc078;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  margin: 0 auto;
  cursor: pointer;
  font-weight: 600;
}
.vacation-day-approved {
  background: #b6e7c9;
  color: #218838;
  border-radius: 100px;
  padding: 0.1em 0.3em;
  box-sizing: border-box;
  border: 1px solid #a3d9b1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  margin: 0 auto;
  cursor: pointer;
}
.vacation-day-pending {
  background: #fff3cd;
  color: #856404;
  border-radius: 100px;
  padding: 0.1em 0.3em;
  box-sizing: border-box;
  border: 1px solid #ffe8a1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 25px;
  height: 25px;
  margin: 0 auto;
  cursor: pointer;
}

/* Leyenda */
.legend-container {
  padding: 0.5rem 1rem;
  background-color: transparent;
  border-top: 1px solid #e9ecef;
}

.legend-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
}

.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  margin: 0.25rem 0.75rem;
}

.legend-box {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  display: inline-block;
  flex-shrink: 0;
}

.legend-label {
  font-size: 0.8rem;
  color: #6c757d;
  white-space: nowrap;
}

@media (max-width: 768px) {
  .legend-container {
    padding: 0.5rem;
  }

  .legend-title {
    display: block;
    width: 100%;
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .legend-item {
    font-size: 0.75rem;
    margin: 0.25rem 0.5rem;
  }

  .legend-box {
    width: 16px;
    height: 16px;
  }
}

/* Estilos personalizados para tooltips */
</style>

<style>
/* Estilos globales para tooltips - sin scoped para que funcionen */
.tooltip.vacation-tooltip {
  opacity: 1 !important;
}

.tooltip.vacation-tooltip .tooltip-inner {
  background-color: #f8f9fa !important;
  color: #212529 !important;
  border: 1px solid #dee2e6 !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  padding: 0.5rem 0.55rem !important;
  font-size: 0.875rem !important;
  max-width: 300px !important;
  text-align: center !important;
  line-height: 1.4 !important;
}

.tooltip.vacation-tooltip .tooltip-inner strong {
  font-weight: 700 !important;
  display: block !important;
  margin-bottom: 0.15rem !important;
}

.tooltip.vacation-tooltip.bs-tooltip-top .arrow::before {
  border-top-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-bottom .arrow::before {
  border-bottom-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-left .arrow::before {
  border-left-color: #dee2e6 !important;
}

.tooltip.vacation-tooltip.bs-tooltip-right .arrow::before {
  border-right-color: #dee2e6 !important;
}
</style>
